import { mdiArrowTopRight, mdiCheck, mdiChevronDown } from '@mdi/js';
import { Icon } from '@mdi/react';
import {
  Root,
  Content,
  Group,
  Item,
  Label,
  Portal,
  Trigger,
} from '@radix-ui/react-dropdown-menu';
import { Link } from 'react-router-dom';
import { keyframes, styled } from 'styled-components';
import { Truncate } from 'components/ui';
import { trackEvent } from 'lib/amplitude';

const slideDownAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateY(-2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const UserMenuButton = styled(Trigger)`
  all: unset;
  color: #252f70;
  font-size: 14px;
  padding: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  display: flex;
  align-items: center;
  border: none;
  background: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: linear-gradient(0deg, #edf1f9 0%, #edf1f9 100%), #fff;
  }
`;

const IconChevron = styled(Icon).attrs({ path: mdiChevronDown })`
  color: #252f70;
  width: 20px;
  height: 20px;
  padding: 3px;
`;

const MenuContent = styled(Content)`
  width: 180px;
  max-height: 270px;
  border-radius: 10px;
  border: 1px #eff3f9;
  background: #fff;
  box-shadow: rgba(117 123 130 / 10%) 0 4px 10px 0;
  padding-top: 18px;
  animation-duration: 400ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;
  animation-name: ${slideDownAndFade};
`;

const MenuGroup = styled(Group)`
  background-color: #fff;
`;

const GroupLabel = styled(Label)`
  color: #252f70;
  font-size: 14px;
  font-weight: 600;
  padding: 0 16px 4px;
`;

const MenuItem = styled(Item)`
  all: unset;
  display: flex;
  align-items: center;
  color: #8c99bc;
  font-size: 12px;
  font-weight: 400;
  margin: 4px;
  padding: 5px 5px 7px;
  border-radius: 4px;
  cursor: pointer;

  &[data-state='checked'] {
    color: #578fe4;
    cursor: default;
  }

  &:hover:not([data-state='checked']) {
    background: linear-gradient(0deg, #edf1f9 0%, #edf1f9 100%), #fff;
  }
`;

const IconChecked = styled(Icon).attrs({
  path: mdiCheck,
})`
  width: 16px;
  height: 16px;
  color: #578fe4;
  margin-right: 7px;
  visibility: hidden;

  &[data-state='checked'] {
    visibility: visible;
  }
`;

const IconLogOut = styled(Icon).attrs({
  path: mdiArrowTopRight,
})`
  width: 16px;
  height: 16px;
  color: inherit;
  margin-left: 5px;
`;

const LogOutButton = styled(Link)`
  color: #252f70;
  font-size: 14px;
  font-weight: 600;
  padding: 18px;
  display: flex;
  align-items: center;
  text-decoration: none;
  border-top: 1px solid #eff3f9;
  border-radius: 0 0 10px 10px;

  &:hover {
    background: linear-gradient(0deg, #edf1f9 0%, #edf1f9 100%), #fff;
  }
`;

const OrgList = styled.section`
  max-height: 194px;
  overflow-y: auto;
`;

const handleLogOut = () => {
  trackEvent('Logged Out');
};

/**
 * UserMenu
 *
 * @param {object} props - the component's props
 * @param {object} props.activeOrg - the active organization
 * @param {Array} props.portfolios - the user's portfolios
 * @param {Function} props.onSelect - the function to handle menu selection
 *
 * @returns {JSX.Element} - the user menu component
 */
export default function UserMenu({ activeOrg, portfolios, onSelect }) {
  return (
    <Root>
      <UserMenuButton aria-label="User Menu">
        <Truncate>{activeOrg?.name}</Truncate>
        <IconChevron />
      </UserMenuButton>
      <Portal>
        <MenuContent side="bottom" align="start">
          <OrgList>
            {portfolios.map((portfolio) => (
              <MenuGroup key={portfolio.key}>
                <GroupLabel>
                  <Truncate>{portfolio.name}</Truncate>
                </GroupLabel>
                <>
                  {portfolio.organizations.map(({ key, name }) => {
                    const dataState = activeOrg?.key === key ? 'checked' : '';
                    return (
                      <MenuItem
                        key={key}
                        value={key}
                        onSelect={() => onSelect({ key, name })}
                        data-state={dataState}
                        disabled={activeOrg?.key === key}
                      >
                        <IconChecked data-state={dataState} />
                        <Truncate>{name}</Truncate>
                      </MenuItem>
                    );
                  })}
                </>
              </MenuGroup>
            ))}
          </OrgList>
          <LogOutButton
            onClick={handleLogOut}
            to={`${import.meta.env.UF_API_SERVER_ADDR}/api/signout`}
          >
            Log out
            <IconLogOut />
          </LogOutButton>
        </MenuContent>
      </Portal>
    </Root>
  );
}
